import {baseApi as api} from './baseApi';

export const addTagTypes = [
  'Admin',
  'Authentication',
  'Event Post Links',
  'Event User Links',
  'Feed',
  'Flagged Posts',
  'Geocoding',
  'Jobs',
  'Layer Admin',
  'Layer Bans',
  'Layer Hidden Posts',
  'Layer Moderation Actions',
  'Layer Usage',
  'Layer',
  'Layer Subscriptions',
  'Likes',
  'Livestream',
  'Media',
  'Messages',
  'Notification',
  'Posts',
  'Region',
  'Reply',
  'Share',
  'User',
] as const;

const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: build => ({
      getFlaggedPosts: build.query<
        GetFlaggedPostsApiResponse,
        GetFlaggedPostsApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/admin/flagged`,
          params: {
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['Admin'],
      }),
      hidePostAsAdmin: build.mutation<
        HidePostAsAdminApiResponse,
        HidePostAsAdminApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/admin/hidden/${queryArg}`,
          method: 'POST',
        }),
        invalidatesTags: ['Admin'],
      }),
      banUser: build.mutation<BanUserApiResponse, BanUserApiArg>({
        query: queryArg => ({
          url: `/api/v1/admin/ban/${queryArg}`,
          method: 'POST',
        }),
        invalidatesTags: ['Admin'],
      }),
      unbanUser: build.mutation<UnbanUserApiResponse, UnbanUserApiArg>({
        query: queryArg => ({
          url: `/api/v1/admin/ban/${queryArg}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Admin'],
      }),
      pinPost: build.mutation<PinPostApiResponse, PinPostApiArg>({
        query: queryArg => ({
          url: `/api/v1/admin/pinned-posts/${queryArg.postId}`,
          method: 'POST',
          params: {
            pin_order: queryArg.pinOrder,
          },
        }),
        invalidatesTags: ['Admin'],
      }),
      unpinPost: build.mutation<UnpinPostApiResponse, UnpinPostApiArg>({
        query: queryArg => ({
          url: `/api/v1/admin/pinned-posts/${queryArg}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Admin'],
      }),
      adminSearchPosts: build.query<
        AdminSearchPostsApiResponse,
        AdminSearchPostsApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/admin/posts`,
          params: {
            q: queryArg.q,
            pinned_only: queryArg.pinnedOnly,
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['Admin'],
      }),
      serveAdminPortalApiV1AdminPortalFullPathGet: build.query<
        ServeAdminPortalApiV1AdminPortalFullPathGetApiResponse,
        ServeAdminPortalApiV1AdminPortalFullPathGetApiArg
      >({
        query: queryArg => ({url: `/api/v1/admin-portal/${queryArg}`}),
        providesTags: ['Admin'],
      }),
      serveAdminRootApiV1AdminGet: build.query<
        ServeAdminRootApiV1AdminGetApiResponse,
        ServeAdminRootApiV1AdminGetApiArg
      >({
        query: () => ({url: `/api/v1/admin`}),
        providesTags: ['Admin'],
      }),
      loginWithUsernameAndPassword: build.mutation<
        LoginWithUsernameAndPasswordApiResponse,
        LoginWithUsernameAndPasswordApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/auth/tokens/password`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Authentication'],
      }),
      loginWithGoogle: build.mutation<
        LoginWithGoogleApiResponse,
        LoginWithGoogleApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/auth/tokens/google`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Authentication'],
      }),
      completeGoogleRegistration: build.mutation<
        CompleteGoogleRegistrationApiResponse,
        CompleteGoogleRegistrationApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/auth/google/complete-registration`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Authentication'],
      }),
      loginWithApple: build.mutation<
        LoginWithAppleApiResponse,
        LoginWithAppleApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/auth/tokens/apple`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Authentication'],
      }),
      completeAppleRegistration: build.mutation<
        CompleteAppleRegistrationApiResponse,
        CompleteAppleRegistrationApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/auth/apple/complete-registration`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Authentication'],
      }),
      passwordResetRequest: build.mutation<
        PasswordResetRequestApiResponse,
        PasswordResetRequestApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/auth/password-reset`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Authentication'],
      }),
      passwordResetVerify: build.mutation<
        PasswordResetVerifyApiResponse,
        PasswordResetVerifyApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/auth/password-reset/verify`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Authentication'],
      }),
      passwordResetConfirm: build.mutation<
        PasswordResetConfirmApiResponse,
        PasswordResetConfirmApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/auth/password-reset/confirm`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Authentication'],
      }),
      getEventPerspectives: build.query<
        GetEventPerspectivesApiResponse,
        GetEventPerspectivesApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/event-post-links/${queryArg.eventId}/perspectives`,
          params: {
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['Event Post Links'],
      }),
      deletePerspective: build.mutation<
        DeletePerspectiveApiResponse,
        DeletePerspectiveApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/event-post-links/perspectives/${queryArg}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Event Post Links'],
      }),
      joinEvent: build.mutation<JoinEventApiResponse, JoinEventApiArg>({
        query: queryArg => ({
          url: `/api/v1/event-user-links`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Event User Links'],
      }),
      leaveEvent: build.mutation<LeaveEventApiResponse, LeaveEventApiArg>({
        query: queryArg => ({
          url: `/api/v1/event-user-links/${queryArg}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Event User Links'],
      }),
      getEventAttendees: build.query<
        GetEventAttendeesApiResponse,
        GetEventAttendeesApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/event-user-links/${queryArg.eventId}/attendees`,
          params: {
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['Event User Links'],
      }),
      getFeed: build.query<GetFeedApiResponse, GetFeedApiArg>({
        query: queryArg => ({
          url: `/api/v1/feed`,
          params: {
            layer_id: queryArg.layerId,
            sort_by: queryArg.sortBy,
            future_events: queryArg.futureEvents,
            ended_events: queryArg.endedEvents,
            ended_livestreams: queryArg.endedLivestreams,
            show_pinned: queryArg.showPinned,
            north_east_latitude: queryArg.northEastLatitude,
            north_east_longitude: queryArg.northEastLongitude,
            south_west_latitude: queryArg.southWestLatitude,
            south_west_longitude: queryArg.southWestLongitude,
            user_longitude: queryArg.userLongitude,
            user_latitude: queryArg.userLatitude,
            post_types: queryArg.postTypes,
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['Feed'],
      }),
      flagPost: build.mutation<FlagPostApiResponse, FlagPostApiArg>({
        query: queryArg => ({
          url: `/api/v1/posts/${queryArg.postId}/flags`,
          method: 'POST',
          body: queryArg.flagPostRequestSchema,
        }),
        invalidatesTags: ['Flagged Posts'],
      }),
      geocodingAutocomplete: build.query<
        GeocodingAutocompleteApiResponse,
        GeocodingAutocompleteApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/geocoding/autocomplete`,
          params: {
            q: queryArg,
          },
        }),
        providesTags: ['Geocoding'],
      }),
      getPlaceDetails: build.query<
        GetPlaceDetailsApiResponse,
        GetPlaceDetailsApiArg
      >({
        query: queryArg => ({url: `/api/v1/geocoding/places/${queryArg}`}),
        providesTags: ['Geocoding'],
      }),
      getJobs: build.query<GetJobsApiResponse, GetJobsApiArg>({
        query: queryArg => ({
          url: `/api/v1/jobs`,
          params: {
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['Jobs'],
      }),
      restartJob: build.mutation<RestartJobApiResponse, RestartJobApiArg>({
        query: queryArg => ({
          url: `/api/v1/jobs/${queryArg}/restart`,
          method: 'POST',
        }),
        invalidatesTags: ['Jobs'],
      }),
      getUserRole: build.query<GetUserRoleApiResponse, GetUserRoleApiArg>({
        query: queryArg => ({url: `/api/v1/layers/${queryArg}/role`}),
        providesTags: ['Layer Admin'],
      }),
      getLayerMembers: build.query<
        GetLayerMembersApiResponse,
        GetLayerMembersApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/layers/${queryArg.layerId}/members`,
          params: {
            roles: queryArg.roles,
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['Layer Admin'],
      }),
      removeLayerModerator: build.mutation<
        RemoveLayerModeratorApiResponse,
        RemoveLayerModeratorApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/layers/moderators/${queryArg}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Layer Admin'],
      }),
      getModeratingLayers: build.query<
        GetModeratingLayersApiResponse,
        GetModeratingLayersApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/layers/moderating`,
          params: {
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['Layer Admin'],
      }),
      claimLayer: build.mutation<ClaimLayerApiResponse, ClaimLayerApiArg>({
        query: queryArg => ({
          url: `/api/v1/layers/${queryArg}/claim`,
          method: 'POST',
        }),
        invalidatesTags: ['Layer Admin'],
      }),
      layerBanUser: build.mutation<LayerBanUserApiResponse, LayerBanUserApiArg>(
        {
          query: queryArg => ({
            url: `/api/v1/layers/${queryArg.layerId}/bans`,
            method: 'POST',
            body: queryArg.banUserRequestSchema,
          }),
          invalidatesTags: ['Layer Bans'],
        },
      ),
      getBannedUsers: build.query<
        GetBannedUsersApiResponse,
        GetBannedUsersApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/layers/${queryArg.layerId}/bans`,
          params: {
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['Layer Bans'],
      }),
      layerUnbanUser: build.mutation<
        LayerUnbanUserApiResponse,
        LayerUnbanUserApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/layers/bans/${queryArg}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Layer Bans'],
      }),
      getHiddenPosts: build.query<
        GetHiddenPostsApiResponse,
        GetHiddenPostsApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/layers/${queryArg.layerId}/hidden-posts`,
          params: {
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['Layer Hidden Posts'],
      }),
      hidePost: build.mutation<HidePostApiResponse, HidePostApiArg>({
        query: queryArg => ({
          url: `/api/v1/layers/${queryArg.layerId}/hidden-posts/${queryArg.postId}`,
          method: 'POST',
          body: queryArg.hidePostRequestSchema,
        }),
        invalidatesTags: ['Layer Hidden Posts'],
      }),
      unhidePost: build.mutation<UnhidePostApiResponse, UnhidePostApiArg>({
        query: queryArg => ({
          url: `/api/v1/layers/hidden-posts/${queryArg}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Layer Hidden Posts'],
      }),
      getLayerModerationActions: build.query<
        GetLayerModerationActionsApiResponse,
        GetLayerModerationActionsApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/layers/${queryArg.layerId}/moderation-actions`,
          params: {
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['Layer Moderation Actions'],
      }),
      incrementLayerUsage: build.mutation<
        IncrementLayerUsageApiResponse,
        IncrementLayerUsageApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/layers/${queryArg}/usage`,
          method: 'POST',
        }),
        invalidatesTags: ['Layer Usage'],
      }),
      getTopLayers: build.query<GetTopLayersApiResponse, GetTopLayersApiArg>({
        query: queryArg => ({
          url: `/api/v1/layers/top`,
          params: {
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['Layer Usage'],
      }),
      createLayer: build.mutation<CreateLayerApiResponse, CreateLayerApiArg>({
        query: queryArg => ({
          url: `/api/v1/layers`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Layer'],
      }),
      getLayers: build.query<GetLayersApiResponse, GetLayersApiArg>({
        query: queryArg => ({
          url: `/api/v1/layers`,
          params: {
            q: queryArg.q,
            similarity_threshold: queryArg.similarityThreshold,
            north_east_latitude: queryArg.northEastLatitude,
            north_east_longitude: queryArg.northEastLongitude,
            south_west_latitude: queryArg.southWestLatitude,
            south_west_longitude: queryArg.southWestLongitude,
            cursor: queryArg.cursor,
            size: queryArg.size,
          },
        }),
        providesTags: ['Layer'],
      }),
      updateLayer: build.mutation<UpdateLayerApiResponse, UpdateLayerApiArg>({
        query: queryArg => ({
          url: `/api/v1/layers/${queryArg.layerId}`,
          method: 'PATCH',
          body: queryArg.createLayerRequestSchema,
        }),
        invalidatesTags: ['Layer'],
      }),
      getLayer: build.query<GetLayerApiResponse, GetLayerApiArg>({
        query: queryArg => ({url: `/api/v1/layers/${queryArg}`}),
        providesTags: ['Layer'],
      }),
      deleteLayer: build.mutation<DeleteLayerApiResponse, DeleteLayerApiArg>({
        query: queryArg => ({
          url: `/api/v1/layers/${queryArg}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Layer'],
      }),
      createSubscription: build.mutation<
        CreateSubscriptionApiResponse,
        CreateSubscriptionApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/layer-subscriptions/${queryArg}`,
          method: 'POST',
        }),
        invalidatesTags: ['Layer Subscriptions'],
      }),
      deleteSubscription: build.mutation<
        DeleteSubscriptionApiResponse,
        DeleteSubscriptionApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/layer-subscriptions/${queryArg}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Layer Subscriptions'],
      }),
      getLayerSubscriptions: build.query<
        GetLayerSubscriptionsApiResponse,
        GetLayerSubscriptionsApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/layer-subscriptions`,
          params: {
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['Layer Subscriptions'],
      }),
      bulkCreateSubscriptions: build.mutation<
        BulkCreateSubscriptionsApiResponse,
        BulkCreateSubscriptionsApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/layer-subscriptions/bulk`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Layer Subscriptions'],
      }),
      likePost: build.mutation<LikePostApiResponse, LikePostApiArg>({
        query: queryArg => ({
          url: `/api/v1/posts/${queryArg}/likes`,
          method: 'POST',
        }),
        invalidatesTags: ['Likes'],
      }),
      unlikePost: build.mutation<UnlikePostApiResponse, UnlikePostApiArg>({
        query: queryArg => ({
          url: `/api/v1/posts/${queryArg}/likes`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Likes'],
      }),
      getActiveLivestream: build.query<
        GetActiveLivestreamApiResponse,
        GetActiveLivestreamApiArg
      >({
        query: () => ({url: `/api/v1/livestreams/active`}),
        providesTags: ['Livestream'],
      }),
      getStreamToken: build.query<
        GetStreamTokenApiResponse,
        GetStreamTokenApiArg
      >({
        query: () => ({url: `/api/v1/livestreams/token`}),
        providesTags: ['Livestream'],
      }),
      processStreamioWebhook: build.mutation<
        ProcessStreamioWebhookApiResponse,
        ProcessStreamioWebhookApiArg
      >({
        query: () => ({
          url: `/api/v1/livestreams/streamio-webhook`,
          method: 'POST',
        }),
        invalidatesTags: ['Livestream'],
      }),
      endLivestream: build.mutation<
        EndLivestreamApiResponse,
        EndLivestreamApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/livestreams/${queryArg}`,
          method: 'PATCH',
        }),
        invalidatesTags: ['Livestream'],
      }),
      getHlsManifest: build.query<
        GetHlsManifestApiResponse,
        GetHlsManifestApiArg
      >({
        query: queryArg => ({
          url: `/api/media/hls/${queryArg.assetId}/${queryArg.path}`,
          params: {
            token: queryArg.token,
            exp: queryArg.exp,
          },
        }),
        providesTags: ['Media'],
      }),
      getConversations: build.query<
        GetConversationsApiResponse,
        GetConversationsApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/messages/conversations`,
          params: {
            since: queryArg.since,
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['Messages'],
      }),
      getConversationMessages: build.query<
        GetConversationMessagesApiResponse,
        GetConversationMessagesApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/messages/conversations/${queryArg.userId}`,
          params: {
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['Messages'],
      }),
      getSentMessages: build.query<
        GetSentMessagesApiResponse,
        GetSentMessagesApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/messages/sent`,
          params: {
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['Messages'],
      }),
      sendLayerInvite: build.mutation<
        SendLayerInviteApiResponse,
        SendLayerInviteApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/messages/send-invite`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Messages'],
      }),
      acceptInvite: build.mutation<AcceptInviteApiResponse, AcceptInviteApiArg>(
        {
          query: queryArg => ({
            url: `/api/v1/messages/${queryArg}/accept`,
            method: 'POST',
          }),
          invalidatesTags: ['Messages'],
        },
      ),
      declineInvite: build.mutation<
        DeclineInviteApiResponse,
        DeclineInviteApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/messages/${queryArg}/decline`,
          method: 'POST',
        }),
        invalidatesTags: ['Messages'],
      }),
      revokeInvite: build.mutation<RevokeInviteApiResponse, RevokeInviteApiArg>(
        {
          query: queryArg => ({
            url: `/api/v1/messages/${queryArg}/revoke`,
            method: 'POST',
          }),
          invalidatesTags: ['Messages'],
        },
      ),
      markAsRead: build.mutation<MarkAsReadApiResponse, MarkAsReadApiArg>({
        query: queryArg => ({
          url: `/api/v1/messages/${queryArg}/read`,
          method: 'PUT',
        }),
        invalidatesTags: ['Messages'],
      }),
      sendDirectMessage: build.mutation<
        SendDirectMessageApiResponse,
        SendDirectMessageApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/messages/direct`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Messages'],
      }),
      validateFcmToken: build.mutation<
        ValidateFcmTokenApiResponse,
        ValidateFcmTokenApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/notifications/fcm-token-validation`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Notification'],
      }),
      registerFcmToken: build.mutation<
        RegisterFcmTokenApiResponse,
        RegisterFcmTokenApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/notifications/fcm-token-registration`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Notification'],
      }),
      getNotifications: build.query<
        GetNotificationsApiResponse,
        GetNotificationsApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/notifications`,
          params: {
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['Notification'],
      }),
      createPost: build.mutation<CreatePostApiResponse, CreatePostApiArg>({
        query: queryArg => ({
          url: `/api/v1/posts`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Posts'],
      }),
      getPosts: build.query<GetPostsApiResponse, GetPostsApiArg>({
        query: queryArg => ({
          url: `/api/v1/posts`,
          params: {
            sort_by: queryArg.sortBy,
            similarity_threshold: queryArg.similarityThreshold,
            q: queryArg.q,
            post_types: queryArg.postTypes,
            cursor: queryArg.cursor,
            size: queryArg.size,
          },
        }),
        providesTags: ['Posts'],
      }),
      getPostMetadata: build.query<
        GetPostMetadataApiResponse,
        GetPostMetadataApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/posts/metadata`,
          params: {
            post_ids: queryArg,
          },
        }),
        providesTags: ['Posts'],
      }),
      getPost: build.query<GetPostApiResponse, GetPostApiArg>({
        query: queryArg => ({url: `/api/v1/posts/${queryArg}`}),
        providesTags: ['Posts'],
      }),
      updatePost: build.mutation<UpdatePostApiResponse, UpdatePostApiArg>({
        query: queryArg => ({
          url: `/api/v1/posts/${queryArg.postId}`,
          method: 'PATCH',
          body: queryArg.updatePostRequestSchema,
        }),
        invalidatesTags: ['Posts'],
      }),
      deletePost: build.mutation<DeletePostApiResponse, DeletePostApiArg>({
        query: queryArg => ({
          url: `/api/v1/posts/${queryArg}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Posts'],
      }),
      getPresignedUrl: build.mutation<
        GetPresignedUrlApiResponse,
        GetPresignedUrlApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/presigned-url`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Posts'],
      }),
      getRegions: build.query<GetRegionsApiResponse, GetRegionsApiArg>({
        query: queryArg => ({
          url: `/api/v1/regions`,
          params: {
            q: queryArg.q,
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['Region'],
      }),
      getRegion: build.query<GetRegionApiResponse, GetRegionApiArg>({
        query: queryArg => ({url: `/api/v1/regions/${queryArg}`}),
        providesTags: ['Region'],
      }),
      createReply: build.mutation<CreateReplyApiResponse, CreateReplyApiArg>({
        query: queryArg => ({
          url: `/api/v1/replies`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Reply'],
      }),
      getReplies: build.query<GetRepliesApiResponse, GetRepliesApiArg>({
        query: queryArg => ({
          url: `/api/v1/replies/post/${queryArg.postId}`,
          params: {
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['Reply'],
      }),
      getRepliesTree: build.query<
        GetRepliesTreeApiResponse,
        GetRepliesTreeApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/replies/post/${queryArg.postId}/tree`,
          params: {
            root_id: queryArg.rootId,
            max_depth: queryArg.maxDepth,
            limit: queryArg.limit,
          },
        }),
        providesTags: ['Reply'],
      }),
      deleteReply: build.mutation<DeleteReplyApiResponse, DeleteReplyApiArg>({
        query: queryArg => ({
          url: `/api/v1/replies/${queryArg}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Reply'],
      }),
      getSharedPublicPost: build.query<
        GetSharedPublicPostApiResponse,
        GetSharedPublicPostApiArg
      >({
        query: queryArg => ({url: `/share/public/post/${queryArg}`}),
        providesTags: ['Share'],
      }),
      sharePostSharePostShareIdGet: build.query<
        SharePostSharePostShareIdGetApiResponse,
        SharePostSharePostShareIdGetApiArg
      >({
        query: queryArg => ({url: `/share/post/${queryArg}`}),
        providesTags: ['Share'],
      }),
      shareLayerHtmlShareLayerShareIdGet: build.query<
        ShareLayerHtmlShareLayerShareIdGetApiResponse,
        ShareLayerHtmlShareLayerShareIdGetApiArg
      >({
        query: queryArg => ({url: `/share/layer/${queryArg}`}),
        providesTags: ['Share'],
      }),
      getSharedPublicLayer: build.query<
        GetSharedPublicLayerApiResponse,
        GetSharedPublicLayerApiArg
      >({
        query: queryArg => ({url: `/share/public/layer/${queryArg}`}),
        providesTags: ['Share'],
      }),
      getSharedPublicFeed: build.query<
        GetSharedPublicFeedApiResponse,
        GetSharedPublicFeedApiArg
      >({
        query: queryArg => ({
          url: `/share/public/feed/${queryArg.shareId}`,
          params: {
            sort_by: queryArg.sortBy,
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['Share'],
      }),
      emailSignupRequest: build.mutation<
        EmailSignupRequestApiResponse,
        EmailSignupRequestApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/users/email-signup/request`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['User'],
      }),
      getNumberOfActiveUsers: build.query<
        GetNumberOfActiveUsersApiResponse,
        GetNumberOfActiveUsersApiArg
      >({
        query: () => ({url: `/api/v1/users/stats/active-users`}),
        providesTags: ['User'],
      }),
      getUsers: build.query<GetUsersApiResponse, GetUsersApiArg>({
        query: queryArg => ({
          url: `/api/v1/users`,
          params: {
            q: queryArg.q,
            cursor: queryArg.cursor,
            size: queryArg.size,
          },
        }),
        providesTags: ['User'],
      }),
      createUserByEmail: build.mutation<
        CreateUserByEmailApiResponse,
        CreateUserByEmailApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/users`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['User'],
      }),
      getCurrentUser: build.query<
        GetCurrentUserApiResponse,
        GetCurrentUserApiArg
      >({
        query: () => ({url: `/api/v1/users/me`}),
        providesTags: ['User'],
      }),
      deleteAccount: build.mutation<
        DeleteAccountApiResponse,
        DeleteAccountApiArg
      >({
        query: () => ({url: `/api/v1/users/me`, method: 'DELETE'}),
        invalidatesTags: ['User'],
      }),
      updateProfile: build.mutation<
        UpdateProfileApiResponse,
        UpdateProfileApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/users/me`,
          method: 'PATCH',
          body: queryArg,
        }),
        invalidatesTags: ['User'],
      }),
      getUser: build.query<GetUserApiResponse, GetUserApiArg>({
        query: queryArg => ({url: `/api/v1/users/${queryArg}`}),
        providesTags: ['User'],
      }),
      followUser: build.mutation<FollowUserApiResponse, FollowUserApiArg>({
        query: queryArg => ({
          url: `/api/v1/users/${queryArg}/followers`,
          method: 'POST',
        }),
        invalidatesTags: ['User'],
      }),
      unfollowUser: build.mutation<UnfollowUserApiResponse, UnfollowUserApiArg>(
        {
          query: queryArg => ({
            url: `/api/v1/users/${queryArg}/followers`,
            method: 'DELETE',
          }),
          invalidatesTags: ['User'],
        },
      ),
      getFollowers: build.query<GetFollowersApiResponse, GetFollowersApiArg>({
        query: queryArg => ({
          url: `/api/v1/users/${queryArg.userId}/followers`,
          params: {
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['User'],
      }),
      getFollowingStatus: build.query<
        GetFollowingStatusApiResponse,
        GetFollowingStatusApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/users/${queryArg}/following-status`,
        }),
        providesTags: ['User'],
      }),
      getFollowing: build.query<GetFollowingApiResponse, GetFollowingApiArg>({
        query: queryArg => ({
          url: `/api/v1/users/${queryArg.userId}/following`,
          params: {
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['User'],
      }),
      getUserPosts: build.query<GetUserPostsApiResponse, GetUserPostsApiArg>({
        query: queryArg => ({
          url: `/api/v1/users/${queryArg.userId}/posts`,
          params: {
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['User'],
      }),
      blockUser: build.mutation<BlockUserApiResponse, BlockUserApiArg>({
        query: queryArg => ({
          url: `/api/v1/users/${queryArg}/blocks`,
          method: 'POST',
        }),
        invalidatesTags: ['User'],
      }),
      unblockUser: build.mutation<UnblockUserApiResponse, UnblockUserApiArg>({
        query: queryArg => ({
          url: `/api/v1/users/${queryArg}/blocks`,
          method: 'DELETE',
        }),
        invalidatesTags: ['User'],
      }),
      getBlockedStatus: build.query<
        GetBlockedStatusApiResponse,
        GetBlockedStatusApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/users/${queryArg}/blocked-status`,
        }),
        providesTags: ['User'],
      }),
      getBlockedUsers: build.query<
        GetBlockedUsersApiResponse,
        GetBlockedUsersApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/users/me/blocks`,
          params: {
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ['User'],
      }),
      serveReactAppPathGet: build.query<
        ServeReactAppPathGetApiResponse,
        ServeReactAppPathGetApiArg
      >({
        query: queryArg => ({url: `/${queryArg}`}),
      }),
    }),
    overrideExisting: false,
  });

export {injectedRtkApi as enhancedApi};

export type GetFlaggedPostsApiResponse =
  /** status 200 Successful Response */ PageFlaggedPostResponseSchema;

export type GetFlaggedPostsApiArg = {
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type HidePostAsAdminApiResponse =
  /** status 200 Successful Response */ any;

export type HidePostAsAdminApiArg = number;

export type BanUserApiResponse = /** status 200 Successful Response */ any;

export type BanUserApiArg = number;

export type UnbanUserApiResponse = /** status 200 Successful Response */ any;

export type UnbanUserApiArg = number;

export type PinPostApiResponse = /** status 200 Successful Response */ any;

export type PinPostApiArg = {
  postId: number;
  /** Position in pinned posts order (0 = first). If not provided, appends to end. */
  pinOrder?: number | null;
};

export type UnpinPostApiResponse = /** status 200 Successful Response */ any;

export type UnpinPostApiArg = number;

export type AdminSearchPostsApiResponse =
  /** status 200 Successful Response */ PagePostResponseSchema;

export type AdminSearchPostsApiArg = {
  /** Search query for post title or message text */
  q?: string | null;
  /** If true, only return pinned posts */
  pinnedOnly?: boolean;
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type ServeAdminPortalApiV1AdminPortalFullPathGetApiResponse =
  /** status 200 Successful Response */ any;

export type ServeAdminPortalApiV1AdminPortalFullPathGetApiArg = string;

export type ServeAdminRootApiV1AdminGetApiResponse =
  /** status 200 Successful Response */ any;

export type ServeAdminRootApiV1AdminGetApiArg = void;

export type LoginWithUsernameAndPasswordApiResponse =
  /** status 200 Successful Response */ TokenSchema;

export type LoginWithUsernameAndPasswordApiArg = UsernamePasswordRequestSchema;

export type LoginWithGoogleApiResponse =
  /** status 200 Successful Response */
  TokenSchema | GoogleRegistrationRequiredSchema;

export type LoginWithGoogleApiArg = GoogleTokenRequestSchema;

export type CompleteGoogleRegistrationApiResponse =
  /** status 200 Successful Response */ TokenSchema;

export type CompleteGoogleRegistrationApiArg = CompleteGoogleRegistrationSchema;

export type LoginWithAppleApiResponse =
  /** status 200 Successful Response */
  TokenSchema | AppleRegistrationRequiredSchema;

export type LoginWithAppleApiArg = AppleTokenRequestSchema;

export type CompleteAppleRegistrationApiResponse =
  /** status 200 Successful Response */ TokenSchema;

export type CompleteAppleRegistrationApiArg = CompleteAppleRegistrationSchema;

export type PasswordResetRequestApiResponse =
  /** status 200 Successful Response */ any;

export type PasswordResetRequestApiArg = PasswordResetRequestSchema;

export type PasswordResetVerifyApiResponse =
  /** status 200 Successful Response */ any;

export type PasswordResetVerifyApiArg = PasswordResetVerifySchema;

export type PasswordResetConfirmApiResponse =
  /** status 200 Successful Response */ any;

export type PasswordResetConfirmApiArg = PasswordResetConfirmSchema;

export type GetEventPerspectivesApiResponse =
  /** status 200 Successful Response */ PagePostResponseSchema;

export type GetEventPerspectivesApiArg = {
  eventId: number;
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type DeletePerspectiveApiResponse = unknown;

export type DeletePerspectiveApiArg = number;

export type JoinEventApiResponse =
  /** status 200 Successful Response */ EventUserLinkResponseSchema;

export type JoinEventApiArg = JoinEventRequestSchema;

export type LeaveEventApiResponse = /** status 200 Successful Response */ any;

export type LeaveEventApiArg = number;

export type GetEventAttendeesApiResponse =
  /** status 200 Successful Response */ PageEventUserLinkResponseSchema;

export type GetEventAttendeesApiArg = {
  eventId: number;
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type GetFeedApiResponse =
  /** status 200 Successful Response */ PagePostResponseSchema;

export type GetFeedApiArg = {
  layerId: string;
  sortBy?: SortByOptions | null;
  futureEvents?: boolean;
  endedEvents?: boolean;
  endedLivestreams?: boolean;
  showPinned?: boolean;
  northEastLatitude?: number | null;
  northEastLongitude?: number | null;
  southWestLatitude?: number | null;
  southWestLongitude?: number | null;
  userLongitude?: number | null;
  userLatitude?: number | null;
  /** Comma-separated list of post types to filter by. Valid values: recorded, event, livestream. Example: post_types=recorded,event */
  postTypes?: string | null;
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type FlagPostApiResponse = /** status 200 Successful Response */ any;

export type FlagPostApiArg = {
  postId: number;
  flagPostRequestSchema: FlagPostRequestSchema;
};

export type GeocodingAutocompleteApiResponse =
  /** status 200 Successful Response */ AutocompleteResponseSchema;

export type GeocodingAutocompleteApiArg =
  /** Search query for address */ string;

export type GetPlaceDetailsApiResponse =
  /** status 200 Successful Response */ PlaceDetailsSchema;

export type GetPlaceDetailsApiArg = string;

export type GetJobsApiResponse =
  /** status 200 Successful Response */ PageJobResponseSchema;

export type GetJobsApiArg = {
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type RestartJobApiResponse =
  /** status 200 Successful Response */ JobResponseSchema;

export type RestartJobApiArg = string;

export type GetUserRoleApiResponse =
  /** status 200 Successful Response */ LayerRoleResponse;

export type GetUserRoleApiArg = number;

export type GetLayerMembersApiResponse =
  /** status 200 Successful Response */ PageLayerAdminResponseSchema;

export type GetLayerMembersApiArg = {
  layerId: number;
  roles?: string;
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type RemoveLayerModeratorApiResponse = unknown;

export type RemoveLayerModeratorApiArg = number;

export type GetModeratingLayersApiResponse =
  /** status 200 Successful Response */ PageLayerResponseSchema;

export type GetModeratingLayersApiArg = {
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type ClaimLayerApiResponse =
  /** status 200 Successful Response */ LayerResponseSchema;

export type ClaimLayerApiArg = number;

export type LayerBanUserApiResponse = /** status 201 Successful Response */ any;

export type LayerBanUserApiArg = {
  layerId: number;
  banUserRequestSchema: BanUserRequestSchema;
};

export type GetBannedUsersApiResponse =
  /** status 200 Successful Response */ PageBannedUserResponseSchema;

export type GetBannedUsersApiArg = {
  layerId: number;
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type LayerUnbanUserApiResponse = unknown;

export type LayerUnbanUserApiArg = number;

export type GetHiddenPostsApiResponse =
  /** status 200 Successful Response */ PageHiddenPostResponseSchema;

export type GetHiddenPostsApiArg = {
  layerId: number;
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type HidePostApiResponse = /** status 201 Successful Response */ any;

export type HidePostApiArg = {
  layerId: number;
  postId: number;
  hidePostRequestSchema: HidePostRequestSchema;
};

export type UnhidePostApiResponse = unknown;

export type UnhidePostApiArg = number;

export type GetLayerModerationActionsApiResponse =
  /** status 200 Successful Response */ PageLayerModerationActionResponseSchema;

export type GetLayerModerationActionsApiArg = {
  layerId: number;
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type IncrementLayerUsageApiResponse =
  /** status 200 Successful Response */ LayerResponseSchema;

export type IncrementLayerUsageApiArg = number;

export type GetTopLayersApiResponse =
  /** status 200 Successful Response */ PageLayerResponseSchema;

export type GetTopLayersApiArg = {
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type CreateLayerApiResponse =
  /** status 200 Successful Response */ LayerResponseSchema;

export type CreateLayerApiArg = CreateLayerRequestSchema;

export type GetLayersApiResponse =
  /** status 200 Successful Response */ CursorPageLayerResponseSchema;

export type GetLayersApiArg = {
  q?: string;
  similarityThreshold?: number;
  northEastLatitude?: number | null;
  northEastLongitude?: number | null;
  southWestLatitude?: number | null;
  southWestLongitude?: number | null;
  /** Cursor for the next page */
  cursor?: string | null;
  /** Page size */
  size?: number;
};

export type UpdateLayerApiResponse =
  /** status 200 Successful Response */ LayerResponseSchema;

export type UpdateLayerApiArg = {
  layerId: number;
  createLayerRequestSchema: CreateLayerRequestSchema;
};

export type GetLayerApiResponse =
  /** status 200 Successful Response */ LayerResponseSchema;

export type GetLayerApiArg = number;

export type DeleteLayerApiResponse = /** status 200 Successful Response */ any;

export type DeleteLayerApiArg = number;

export type CreateSubscriptionApiResponse =
  /** status 200 Successful Response */ any;

export type CreateSubscriptionApiArg = number;

export type DeleteSubscriptionApiResponse =
  /** status 200 Successful Response */ any;

export type DeleteSubscriptionApiArg = number;

export type GetLayerSubscriptionsApiResponse =
  /** status 200 Successful Response */ PageLayerResponseSchema;

export type GetLayerSubscriptionsApiArg = {
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type BulkCreateSubscriptionsApiResponse =
  /** status 200 Successful Response */ any;

export type BulkCreateSubscriptionsApiArg = BulkLayerSubscriptionSchema;

export type LikePostApiResponse =
  /** status 200 Successful Response */ PostResponseSchema;

export type LikePostApiArg = number;

export type UnlikePostApiResponse =
  /** status 200 Successful Response */ PostResponseSchema;

export type UnlikePostApiArg = number;

export type GetActiveLivestreamApiResponse =
  /** status 200 Successful Response */ ActiveLivestreamResponseSchema | null;

export type GetActiveLivestreamApiArg = void;

export type GetStreamTokenApiResponse =
  /** status 200 Successful Response */ StreamTokenSchema;

export type GetStreamTokenApiArg = void;

export type ProcessStreamioWebhookApiResponse =
  /** status 200 Successful Response */ any;

export type ProcessStreamioWebhookApiArg = void;

export type EndLivestreamApiResponse =
  /** status 200 Successful Response */ any;

export type EndLivestreamApiArg = number;

export type GetHlsManifestApiResponse =
  /** status 200 Successful Response */ any;

export type GetHlsManifestApiArg = {
  assetId: string;
  path: string;
  token: string;
  exp: number;
};

export type GetConversationsApiResponse =
  /** status 200 Successful Response */ PageConversationResponse;

export type GetConversationsApiArg = {
  since?: string | null;
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type GetConversationMessagesApiResponse =
  /** status 200 Successful Response */ PageMessageResponse;

export type GetConversationMessagesApiArg = {
  userId: number;
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type GetSentMessagesApiResponse =
  /** status 200 Successful Response */ PageMessageResponse;

export type GetSentMessagesApiArg = {
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type SendLayerInviteApiResponse =
  /** status 200 Successful Response */ MessageResponse;

export type SendLayerInviteApiArg = SendInviteRequest;

export type AcceptInviteApiResponse = /** status 200 Successful Response */ any;

export type AcceptInviteApiArg = number;

export type DeclineInviteApiResponse =
  /** status 200 Successful Response */ any;

export type DeclineInviteApiArg = number;

export type RevokeInviteApiResponse = /** status 200 Successful Response */ any;

export type RevokeInviteApiArg = number;

export type MarkAsReadApiResponse = /** status 200 Successful Response */ any;

export type MarkAsReadApiArg = number;

export type SendDirectMessageApiResponse =
  /** status 200 Successful Response */ MessageResponse;

export type SendDirectMessageApiArg = DirectMessageRequest;

export type ValidateFcmTokenApiResponse =
  /** status 200 Successful Response */ CheckDeviceTokenResponseSchema;

export type ValidateFcmTokenApiArg = CheckDeviceTokenRequestSchema;

export type RegisterFcmTokenApiResponse =
  /** status 200 Successful Response */ any;

export type RegisterFcmTokenApiArg = RegisterDeviceRequestSchema;

export type GetNotificationsApiResponse =
  /** status 200 Successful Response */ PageNotificationResponseSchema;

export type GetNotificationsApiArg = {
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type CreatePostApiResponse =
  /** status 200 Successful Response */ PostResponseSchema;

export type CreatePostApiArg = CreatePostRequestSchema;

export type GetPostsApiResponse =
  /** status 200 Successful Response */ CursorPagePostResponseSchema;

export type GetPostsApiArg = {
  sortBy?: SortByOptions | null;
  similarityThreshold?: number;
  q: string;
  postTypes?: PostTypes[] | null;
  /** Cursor for the next page */
  cursor?: string | null;
  /** Page size */
  size?: number;
};

export type GetPostMetadataApiResponse = /** status 200 Successful Response */ {
  [key: string]: PostMetadataResponseSchema;
};

export type GetPostMetadataApiArg = string;

export type GetPostApiResponse =
  /** status 200 Successful Response */ PostResponseSchema;

export type GetPostApiArg = number;

export type UpdatePostApiResponse =
  /** status 200 Successful Response */ PostResponseSchema;

export type UpdatePostApiArg = {
  postId: number;
  updatePostRequestSchema: UpdatePostRequestSchema;
};

export type DeletePostApiResponse = /** status 200 Successful Response */ any;

export type DeletePostApiArg = number;

export type GetPresignedUrlApiResponse =
  /** status 200 Successful Response */ PresignedUrlResponseSchema;

export type GetPresignedUrlApiArg = UploadRequestSchema;

export type GetRegionsApiResponse =
  /** status 200 Successful Response */ PageRegionResponseSchema;

export type GetRegionsApiArg = {
  q: string;
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type GetRegionApiResponse =
  /** status 200 Successful Response */ RegionResponseSchema;

export type GetRegionApiArg = number;

export type CreateReplyApiResponse =
  /** status 200 Successful Response */ ReplyResponseSchema;

export type CreateReplyApiArg = CreateReplyRequestSchema;

export type GetRepliesApiResponse =
  /** status 200 Successful Response */ PageReplyResponseSchema;

export type GetRepliesApiArg = {
  postId: number;
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type GetRepliesTreeApiResponse =
  /** status 200 Successful Response */ ReplyTreeResponseSchema;

export type GetRepliesTreeApiArg = {
  postId: number;
  rootId?: number | null;
  maxDepth?: number;
  limit?: number;
};

export type DeleteReplyApiResponse =
  /** status 200 Successful Response */ ReplyResponseSchema;

export type DeleteReplyApiArg = number;

export type GetSharedPublicPostApiResponse =
  /** status 200 Successful Response */ PostResponseSchema;

export type GetSharedPublicPostApiArg = string;

export type SharePostSharePostShareIdGetApiResponse =
  /** status 200 Successful Response */ string;

export type SharePostSharePostShareIdGetApiArg = string;

export type ShareLayerHtmlShareLayerShareIdGetApiResponse =
  /** status 200 Successful Response */ string;

export type ShareLayerHtmlShareLayerShareIdGetApiArg = string;

export type GetSharedPublicLayerApiResponse =
  /** status 200 Successful Response */ LayerResponseSchema;

export type GetSharedPublicLayerApiArg = string;

export type GetSharedPublicFeedApiResponse =
  /** status 200 Successful Response */ PagePostResponseSchema;

export type GetSharedPublicFeedApiArg = {
  shareId: string;
  sortBy?: SortByOptions | null;
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type EmailSignupRequestApiResponse =
  /** status 200 Successful Response */ any;

export type EmailSignupRequestApiArg = EmailSignupRequestSchema;

export type GetNumberOfActiveUsersApiResponse =
  /** status 200 Successful Response */ NumberOfActiveUsersResponseSchema;

export type GetNumberOfActiveUsersApiArg = void;

export type GetUsersApiResponse =
  /** status 200 Successful Response */ CursorPageUserResponseSchema;

export type GetUsersApiArg = {
  q?: string;
  /** Cursor for the next page */
  cursor?: string | null;
  /** Page size */
  size?: number;
};

export type CreateUserByEmailApiResponse =
  /** status 200 Successful Response */ UserResponseSchema;

export type CreateUserByEmailApiArg = CreateUserByEmailRequestSchema;

export type GetCurrentUserApiResponse =
  /** status 200 Successful Response */ CurrentUserResponseSchema;

export type GetCurrentUserApiArg = void;

export type DeleteAccountApiResponse =
  /** status 200 Successful Response */ any;

export type DeleteAccountApiArg = void;

export type UpdateProfileApiResponse =
  /** status 200 Successful Response */ CurrentUserResponseSchema;

export type UpdateProfileApiArg = UpdateUserRequestSchema;

export type GetUserApiResponse =
  /** status 200 Successful Response */ UserResponseSchema;

export type GetUserApiArg = number;

export type FollowUserApiResponse = /** status 200 Successful Response */ any;

export type FollowUserApiArg = number;

export type UnfollowUserApiResponse = /** status 200 Successful Response */ any;

export type UnfollowUserApiArg = number;

export type GetFollowersApiResponse =
  /** status 200 Successful Response */ PageUserResponseSchema;

export type GetFollowersApiArg = {
  userId: number;
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type GetFollowingStatusApiResponse =
  /** status 200 Successful Response */ FollowingResponseSchema;

export type GetFollowingStatusApiArg = number;

export type GetFollowingApiResponse =
  /** status 200 Successful Response */ PageUserResponseSchema;

export type GetFollowingApiArg = {
  userId: number;
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type GetUserPostsApiResponse =
  /** status 200 Successful Response */ PagePostResponseSchema;

export type GetUserPostsApiArg = {
  userId: number;
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type BlockUserApiResponse = /** status 200 Successful Response */ any;

export type BlockUserApiArg = number;

export type UnblockUserApiResponse = /** status 200 Successful Response */ any;

export type UnblockUserApiArg = number;

export type GetBlockedStatusApiResponse =
  /** status 200 Successful Response */ BlockedResponseSchema;

export type GetBlockedStatusApiArg = number;

export type GetBlockedUsersApiResponse =
  /** status 200 Successful Response */ PageUserResponseSchema;

export type GetBlockedUsersApiArg = {
  /** Page number */
  page?: number;
  /** Page size */
  size?: number;
};

export type ServeReactAppPathGetApiResponse =
  /** status 200 Successful Response */ any;

export type ServeReactAppPathGetApiArg = string;

export type PostTypes = 'recorded' | 'livestream' | 'event';

export type PostPrivacyOptions = 'public' | 'private' | 'following';

export type LayerVisibility = 'public' | 'private';

export type LayerPostingPermission = 'open' | 'members_only';

export type GeoJsonGeometry = {
  /** GeoJSON geometry type */
  type?: string;
  coordinates: number[][][] | number[][][][];
};

export type LayerResponseSchema = {
  id: number;
  share_id: string;
  title: string;
  rawtext: string;
  icon: string;
  created_at: string;
  usage_count: number | null;
  description: string | null;
  ai_content_enabled: boolean | null;
  visibility: LayerVisibility | null;
  posting_permission: LayerPostingPermission | null;
  owner_id: number | null;
  region_id: number | null;
  geom: GeoJsonGeometry | null;
  is_global: boolean | null;
};

export type ProcessingState = 'pending' | 'processing' | 'completed' | 'failed';

export type PostResponseSchema = {
  id: number;
  share_id: string;
  username: string;
  user_id: number;
  post_type: PostTypes;
  longitude: number;
  latitude: number;
  timestamp: string;
  icon?: string | null;
  title?: string | null;
  audio_url?: string | null;
  photo_url?: string | null;
  photo_url_compressed?: string | null;
  marker_thumbnail?: string | null;
  video_url?: string | null;
  has_rotation_metadata?: boolean;
  message_text?: string | null;
  livestream_id?: string | null;
  chat_channel_id?: string | null;
  profile_pic?: string | null;
  privacy_status?: PostPrivacyOptions | null;
  layer?: LayerResponseSchema | null;
  hide_reason?: string | null;
  m3u8_url?: string | null;
  processing_state?: ProcessingState | null;
  starts_at?: string | null;
  ends_at?: string | null;
  external_perspectives_allowed?: boolean;
  perspective_allowed_distance_km?: number | null;
  pin_order?: number | null;
  score?: number | null;
};

export type FlaggedPostResponseSchema = {
  id: number;
  post_id: number;
  user_id: number;
  reason: string;
  flagged_at: string;
  post: PostResponseSchema;
  hidden_by: any | null;
};

export type PageFlaggedPostResponseSchema = {
  items: FlaggedPostResponseSchema[];
  total: number | null;
  page: number | null;
  size: number | null;
  pages?: number | null;
};

export type ValidationError = {
  loc: (string | number)[];
  msg: string;
  type: string;
};

export type HttpValidationError = {
  detail?: ValidationError[];
};

export type PagePostResponseSchema = {
  items: PostResponseSchema[];
  total: number | null;
  page: number | null;
  size: number | null;
  pages?: number | null;
};

export type TokenSchema = {
  access_token: string;
  token_type: string;
};

export type UsernamePasswordRequestSchema = {
  username: string;
  password: string;
};

export type GoogleUserSchema = {
  email: string;
  name: string;
  picture: string | null;
  sub: string;
};

export type GoogleRegistrationRequiredSchema = {
  registration_required?: boolean;
  google_profile: GoogleUserSchema;
  temp_token: string;
};

export type GoogleTokenRequestSchema = {
  id_token: string;
};

export type CompleteGoogleRegistrationSchema = {
  temp_token: string;
  username: string;
};

export type AppleUserSchema = {
  email: string;
  sub: string;
  email_verified?: boolean;
};

export type AppleRegistrationRequiredSchema = {
  registration_required?: boolean;
  apple_profile: AppleUserSchema;
  temp_token: string;
};

export type AppleTokenRequestSchema = {
  id_token: string;
};

export type CompleteAppleRegistrationSchema = {
  temp_token: string;
  username: string;
};

export type PasswordResetRequestSchema = {
  email: string;
};

export type PasswordResetVerifySchema = {
  code: string;
};

export type PasswordResetConfirmSchema = {
  code: string;
  new_password: string;
};

export type UserResponseSchema = {
  id: number;
  username: string | null;
  followers_count: number | null;
  followees_count: number | null;
  posts_count: number | null;
  profile_pic: string | null;
  disabled: boolean | null;
  is_following: boolean | null;
  joined_date: string | null;
  is_admin: boolean | null;
  layer_id?: number | null;
  role?: string | null;
};

export type EventUserLinkResponseSchema = {
  id: string;
  event_id: number;
  user_id: number;
  status: string;
  created_at: string;
  user: UserResponseSchema;
};

export type JoinEventRequestSchema = {
  event_id: number;
};

export type PageEventUserLinkResponseSchema = {
  items: EventUserLinkResponseSchema[];
  total: number | null;
  page: number | null;
  size: number | null;
  pages?: number | null;
};

export type SortByOptions =
  | 'recent'
  | 'trending'
  | 'top'
  | 'relevant'
  | 'nearest'
  | 'upcoming'
  | 'ended';

export type FlagPostRequestSchema = {
  reason: string;
};

export type AutocompleteResultSchema = {
  /** Unique Google place identifier */
  place_id: string;
  /** Full text description of the place */
  description: string;
  /** Main text (e.g., business name or street) */
  main_text?: string | null;
  /** Secondary text (e.g., city, state) */
  secondary_text?: string | null;
};

export type AutocompleteResponseSchema = {
  results: AutocompleteResultSchema[];
};

export type PlaceDetailsSchema = {
  /** Unique Google place identifier */
  place_id: string;
  /** Place name */
  name?: string | null;
  /** Full formatted address */
  formatted_address: string;
  /** Latitude coordinate */
  latitude: number;
  /** Longitude coordinate */
  longitude: number;
  /** City/locality */
  city?: string | null;
  /** State/administrative area */
  state?: string | null;
  /** Country */
  country?: string | null;
  /** Postal/ZIP code */
  postal_code?: string | null;
};

export type JobResponseSchema = {
  id: string;
  job_type: string;
  resource_type: string;
  resource_id: number;
  status: string;
  attempts: number;
  last_error: string | null;
  created_at: string;
  updated_at: string;
  started_at: string | null;
  completed_at: string | null;
};

export type PageJobResponseSchema = {
  items: JobResponseSchema[];
  total: number | null;
  page: number | null;
  size: number | null;
  pages?: number | null;
};

export type LayerRole = 'owner' | 'moderator' | 'member';

export type LayerRoleResponse = {
  role: LayerRole | null;
};

export type LayerAdminResponseSchema = {
  id: number;
  user_id: number;
  layer_id: number;
  role: LayerRole;
  joined_at: string;
  user: UserResponseSchema;
};

export type PageLayerAdminResponseSchema = {
  items: LayerAdminResponseSchema[];
  total: number | null;
  page: number | null;
  size: number | null;
  pages?: number | null;
};

export type PageLayerResponseSchema = {
  items: LayerResponseSchema[];
  total: number | null;
  page: number | null;
  size: number | null;
  pages?: number | null;
};

export type BanUserRequestSchema = {
  target_user_id: number;
  reason?: string | null;
};

export type BannedUserResponseSchema = {
  id: number;
  reason: string;
  created_at: string;
  user: UserResponseSchema;
};

export type PageBannedUserResponseSchema = {
  items: BannedUserResponseSchema[];
  total: number | null;
  page: number | null;
  size: number | null;
  pages?: number | null;
};

export type HiddenPostResponseSchema = {
  id: number;
  reason: string;
  hidden_at: string;
  post: PostResponseSchema;
};

export type PageHiddenPostResponseSchema = {
  items: HiddenPostResponseSchema[];
  total: number | null;
  page: number | null;
  size: number | null;
  pages?: number | null;
};

export type HidePostRequestSchema = {
  reason?: string | null;
};

export type LayerModerationActionResponseSchema = {
  id: number;
  layer_id: number;
  moderator: UserResponseSchema;
  action_type: string;
  target_user?: UserResponseSchema | null;
  target_post?: PostResponseSchema | null;
  reason?: string | null;
  created_at: string;
};

export type PageLayerModerationActionResponseSchema = {
  items: LayerModerationActionResponseSchema[];
  total: number | null;
  page: number | null;
  size: number | null;
  pages?: number | null;
};

export type CreateLayerRequestSchema = {
  title: string;
  rawtext: string;
  visibility: LayerVisibility;
  posting_permission: LayerPostingPermission;
  ai_content_enabled?: boolean;
  region_id?: number | null;
  geom?: object | null;
  is_global?: boolean;
};

export type CursorPageLayerResponseSchema = {
  items: LayerResponseSchema[];
  /** Total items */
  total?: number | null;
  /** Cursor to refetch the current page */
  current_page?: string | null;
  /** Cursor to refetch the current page starting from the last item */
  current_page_backwards?: string | null;
  /** Cursor for the previous page */
  previous_page?: string | null;
  /** Cursor for the next page */
  next_page?: string | null;
};

export type BulkLayerSubscriptionSchema = {
  layer_ids: number[];
};

export type ActiveLivestreamResponseSchema = {
  post_id: number;
  livestream_id: string;
  chat_channel_id: string;
  stream_token: string;
  title?: string | null;
};

export type StreamTokenSchema = {
  stream_token: string;
  user_id?: number | null;
};

export type MessageType = 'invite' | 'direct';

export type MessageStatus = 'pending' | 'accepted' | 'declined' | 'revoked';

export type MessageResponse = {
  message_type: MessageType;
  content: string;
  layer_id?: number | null;
  role?: LayerRole | null;
  id: number;
  sender_id: number;
  recipient_id: number;
  sent_at: string;
  read_at?: string | null;
  processed_at?: string | null;
  is_active: boolean;
  status?: MessageStatus | null;
};

export type ConversationResponse = {
  id: number;
  user1_id: number;
  user2_id: number;
  latest_message_id: number | null;
  unread_count: number;
  updated_at: string;
  latest_message: MessageResponse | null;
  user1: UserResponseSchema;
  user2: UserResponseSchema;
};

export type PageConversationResponse = {
  items: ConversationResponse[];
  total: number | null;
  page: number | null;
  size: number | null;
  pages?: number | null;
};

export type PageMessageResponse = {
  items: MessageResponse[];
  total: number | null;
  page: number | null;
  size: number | null;
  pages?: number | null;
};

export type SendInviteRequest = {
  recipient_id: number;
  layer_id: number;
  role: LayerRole;
  message?: string | null;
};

export type DirectMessageRequest = {
  recipient_id: number;
  content: string;
};

export type CheckDeviceTokenResponseSchema = {
  valid: boolean;
};

export type CheckDeviceTokenRequestSchema = {
  device_token: string;
};

export type RegisterDeviceRequestSchema = {
  device_token: string;
  device_type: string;
};

export type NotificationResponseSchema = {
  id: number;
  title: string;
  body: string | null;
  post_id: number | null;
  livestream_id: string | null;
  chat_channel_id?: string | null;
  notification_type: string | null;
  created_at: string;
};

export type PageNotificationResponseSchema = {
  items: NotificationResponseSchema[];
  total: number | null;
  page: number | null;
  size: number | null;
  pages?: number | null;
};

export type VideoFrameSchema = {
  url: string;
  timestamp_seconds: number;
};

export type ValidIcons =
  | 'newspaper'
  | 'alert'
  | 'fire'
  | 'police-badge'
  | 'ambulance'
  | 'heart'
  | 'hospital'
  | 'pill'
  | 'virus'
  | 'brain'
  | 'school'
  | 'book'
  | 'school-outline'
  | 'bank'
  | 'cash'
  | 'chart-line'
  | 'store'
  | 'shopping'
  | 'food'
  | 'pizza'
  | 'coffee'
  | 'beer'
  | 'glass-wine'
  | 'silverware-fork-knife'
  | 'music'
  | 'piano'
  | 'movie'
  | 'theater'
  | 'television'
  | 'palette'
  | 'brush'
  | 'camera'
  | 'image'
  | 'microphone'
  | 'radio'
  | 'basketball'
  | 'soccer'
  | 'football'
  | 'tennis'
  | 'bike'
  | 'run'
  | 'hiking'
  | 'swim'
  | 'ski'
  | 'beach'
  | 'forest'
  | 'tree'
  | 'flower'
  | 'leaf'
  | 'dog'
  | 'cat'
  | 'paw'
  | 'bird'
  | 'fish'
  | 'horse'
  | 'butterfly'
  | 'weather-sunny'
  | 'weather-rainy'
  | 'weather-snowy'
  | 'weather-windy'
  | 'weather-hurricane'
  | 'wave'
  | 'tsunami'
  | 'fire-alert'
  | 'water'
  | 'earth'
  | 'map-marker'
  | 'airplane'
  | 'train'
  | 'bus'
  | 'car'
  | 'bicycle'
  | 'ferry'
  | 'rocket'
  | 'satellite'
  | 'home'
  | 'office-building'
  | 'factory'
  | 'warehouse'
  | 'church'
  | 'mosque'
  | 'menorah'
  | 'castle'
  | 'bridge'
  | 'tower-fire'
  | 'road-variant'
  | 'traffic-light'
  | 'hammer-wrench'
  | 'wifi'
  | 'cellphone'
  | 'laptop'
  | 'desktop-classic'
  | 'printer'
  | 'robot'
  | 'atom'
  | 'dna'
  | 'microscope'
  | 'telescope'
  | 'scale-balance'
  | 'gavel'
  | 'account-group'
  | 'baby'
  | 'human-pregnant'
  | 'ring'
  | 'cake-variant'
  | 'gift'
  | 'headphones';

export type CreatePostRequestSchema = {
  post_type: PostTypes;
  audio_url?: string | null;
  photo_url?: string | null;
  video_url?: string | null;
  video_frames?: VideoFrameSchema[] | null;
  video_transcription?: string | null;
  message_text?: string | null;
  title?: string | null;
  longitude: number;
  latitude: number;
  privacy_status?: PostPrivacyOptions;
  layer_id?: number | null;
  icon?: ValidIcons | null;
  starts_at?: string | null;
  ends_at?: string | null;
  event_post_id?: number | null;
  external_perspectives_allowed?: boolean;
  perspective_allowed_distance_km?: number | null;
};

export type CursorPagePostResponseSchema = {
  items: PostResponseSchema[];
  /** Total items */
  total?: number | null;
  /** Cursor to refetch the current page */
  current_page?: string | null;
  /** Cursor to refetch the current page starting from the last item */
  current_page_backwards?: string | null;
  /** Cursor for the previous page */
  previous_page?: string | null;
  /** Cursor for the next page */
  next_page?: string | null;
};

export type StreamStatus = 'live' | 'disconnected' | 'ended';

export type PostMetadataResponseSchema = {
  likes_count: number;
  liked_by_me: boolean;
  replies_count: number;
  blurred: boolean;
  attending?: boolean | null;
  attendee_count?: number | null;
  stream_status?: StreamStatus | null;
  current_viewer_count?: number | null;
  total_viewer_count?: number | null;
};

export type UpdatePostRequestSchema = {
  layer_id?: number | null;
  message_text?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  title?: string | null;
  photo_url?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  external_perspectives_allowed?: boolean | null;
  perspective_allowed_distance_km?: number | null;
  end_event_now?: boolean;
};

export type PresignedUrlResponseSchema = {
  file_name: string;
  presigned_url: string;
};

export type UploadRequestSchema = {
  content_type: string;
};

export type RegionResponseSchema = {
  id: number;
  name: string;
  region_type: string;
  area_km2: number | null;
  geom: object | null;
  centroid: object | null;
};

export type PageRegionResponseSchema = {
  items: RegionResponseSchema[];
  total: number | null;
  page: number | null;
  size: number | null;
  pages?: number | null;
};

export type ReplyResponseSchema = {
  id: number;
  post_id: number;
  parent_id?: number | null;
  soft_deleted?: boolean;
  user_id: number;
  username: string;
  timestamp: string;
  audio_url?: string | null;
  photo_url?: string | null;
  video_url?: string | null;
  message_text?: string | null;
  profile_pic?: string | null;
};

export type CreateReplyRequestSchema = {
  post_id: number;
  parent_id?: number | null;
  audio_url?: string | null;
  photo_url?: string | null;
  video_url?: string | null;
  message_text?: string | null;
};

export type PageReplyResponseSchema = {
  items: ReplyResponseSchema[];
  total: number | null;
  page: number | null;
  size: number | null;
  pages?: number | null;
};

export type ReplyTreeNodeSchema = {
  id: number;
  post_id: number;
  parent_id?: number | null;
  soft_deleted?: boolean;
  user_id: number;
  username: string;
  timestamp: string;
  audio_url?: string | null;
  photo_url?: string | null;
  video_url?: string | null;
  message_text?: string | null;
  profile_pic?: string | null;
  visible: boolean;
  depth: number;
  child_count: number;
};

export type ContinuationNodeSchema = {
  id: number;
  child_count: number;
};

export type ReplyTreeResponseSchema = {
  replies: ReplyTreeNodeSchema[];
  continuation_nodes: ContinuationNodeSchema[];
  total_count: number;
};

export type EmailSignupRequestSchema = {
  email: string;
};

export type NumberOfActiveUsersResponseSchema = {
  number_of_active_users: number;
};

export type CursorPageUserResponseSchema = {
  items: UserResponseSchema[];
  /** Total items */
  total?: number | null;
  /** Cursor to refetch the current page */
  current_page?: string | null;
  /** Cursor to refetch the current page starting from the last item */
  current_page_backwards?: string | null;
  /** Cursor for the previous page */
  previous_page?: string | null;
  /** Cursor for the next page */
  next_page?: string | null;
};

export type CreateUserByEmailRequestSchema = {
  username: string;
  password: string;
  email: string;
  code: string;
};

export type CurrentUserResponseSchema = {
  id: number;
  username: string | null;
  followers_count: number | null;
  followees_count: number | null;
  posts_count: number | null;
  profile_pic: string | null;
  disabled: boolean | null;
  is_following: boolean | null;
  joined_date: string | null;
  is_admin: boolean | null;
  layer_id?: number | null;
  role?: string | null;
  email: string;
};

export type UpdateUserRequestSchema = {
  username?: string | null;
  email?: string | null;
  password?: string | null;
  disabled?: boolean | null;
  profile_pic?: string | null;
};

export type PageUserResponseSchema = {
  items: UserResponseSchema[];
  total: number | null;
  page: number | null;
  size: number | null;
  pages?: number | null;
};

export type FollowingResponseSchema = {
  is_following: boolean;
};

export type BlockedResponseSchema = {
  is_blocked: boolean;
};

export const {
  useGetFlaggedPostsQuery,
  useHidePostAsAdminMutation,
  useBanUserMutation,
  useUnbanUserMutation,
  usePinPostMutation,
  useUnpinPostMutation,
  useAdminSearchPostsQuery,
  useServeAdminPortalApiV1AdminPortalFullPathGetQuery,
  useServeAdminRootApiV1AdminGetQuery,
  useLoginWithUsernameAndPasswordMutation,
  useLoginWithGoogleMutation,
  useCompleteGoogleRegistrationMutation,
  useLoginWithAppleMutation,
  useCompleteAppleRegistrationMutation,
  usePasswordResetRequestMutation,
  usePasswordResetVerifyMutation,
  usePasswordResetConfirmMutation,
  useGetEventPerspectivesQuery,
  useDeletePerspectiveMutation,
  useJoinEventMutation,
  useLeaveEventMutation,
  useGetEventAttendeesQuery,
  useGetFeedQuery,
  useFlagPostMutation,
  useGeocodingAutocompleteQuery,
  useGetPlaceDetailsQuery,
  useGetJobsQuery,
  useRestartJobMutation,
  useGetUserRoleQuery,
  useGetLayerMembersQuery,
  useRemoveLayerModeratorMutation,
  useGetModeratingLayersQuery,
  useClaimLayerMutation,
  useLayerBanUserMutation,
  useGetBannedUsersQuery,
  useLayerUnbanUserMutation,
  useGetHiddenPostsQuery,
  useHidePostMutation,
  useUnhidePostMutation,
  useGetLayerModerationActionsQuery,
  useIncrementLayerUsageMutation,
  useGetTopLayersQuery,
  useCreateLayerMutation,
  useGetLayersQuery,
  useUpdateLayerMutation,
  useGetLayerQuery,
  useDeleteLayerMutation,
  useCreateSubscriptionMutation,
  useDeleteSubscriptionMutation,
  useGetLayerSubscriptionsQuery,
  useBulkCreateSubscriptionsMutation,
  useLikePostMutation,
  useUnlikePostMutation,
  useGetActiveLivestreamQuery,
  useGetStreamTokenQuery,
  useProcessStreamioWebhookMutation,
  useEndLivestreamMutation,
  useGetHlsManifestQuery,
  useGetConversationsQuery,
  useGetConversationMessagesQuery,
  useGetSentMessagesQuery,
  useSendLayerInviteMutation,
  useAcceptInviteMutation,
  useDeclineInviteMutation,
  useRevokeInviteMutation,
  useMarkAsReadMutation,
  useSendDirectMessageMutation,
  useValidateFcmTokenMutation,
  useRegisterFcmTokenMutation,
  useGetNotificationsQuery,
  useCreatePostMutation,
  useGetPostsQuery,
  useGetPostMetadataQuery,
  useGetPostQuery,
  useUpdatePostMutation,
  useDeletePostMutation,
  useGetPresignedUrlMutation,
  useGetRegionsQuery,
  useGetRegionQuery,
  useCreateReplyMutation,
  useGetRepliesQuery,
  useGetRepliesTreeQuery,
  useDeleteReplyMutation,
  useGetSharedPublicPostQuery,
  useSharePostSharePostShareIdGetQuery,
  useShareLayerHtmlShareLayerShareIdGetQuery,
  useGetSharedPublicLayerQuery,
  useGetSharedPublicFeedQuery,
  useEmailSignupRequestMutation,
  useGetNumberOfActiveUsersQuery,
  useGetUsersQuery,
  useCreateUserByEmailMutation,
  useGetCurrentUserQuery,
  useDeleteAccountMutation,
  useUpdateProfileMutation,
  useGetUserQuery,
  useFollowUserMutation,
  useUnfollowUserMutation,
  useGetFollowersQuery,
  useGetFollowingStatusQuery,
  useGetFollowingQuery,
  useGetUserPostsQuery,
  useBlockUserMutation,
  useUnblockUserMutation,
  useGetBlockedStatusQuery,
  useGetBlockedUsersQuery,
  useServeReactAppPathGetQuery,
} = injectedRtkApi;
